import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ArrowRight, Mail } from 'lucide-react';
import type { Language } from './caseStudyData';

export const contactEmail = 'hello@heglo.io';

const copy = {
  en: {
    label: 'Contact us', title: 'Let’s talk about your startup.',
    description: 'Tell us the platform you want to build, who will use it and where you want to launch.',
    name: 'Your name', email: 'Email address', message: 'How can we help?',
    send: 'Send message', sending: 'Sending…', sent: 'Your message has been sent. Thank you for getting in touch.',
    error: 'Your message could not be sent. Please try again or email hello@heglo.io.',
    rateLimit: 'Too many messages. Please wait a minute or email hello@heglo.io.',
    unavailable: 'Please email us directly at hello@heglo.io.',
    privacy: 'We’ll use your details to respond to your enquiry.',
  },
  ar: {
    label: 'تواصل معنا', title: 'حدثنا عن فكرة مشروعك الناشئ.',
    description: 'ما المنصة التي تريد بناءها؟ ومن سيستخدمها؟ وفي أي سوق تريد إطلاقها؟',
    name: 'اسمك', email: 'البريد الإلكتروني', message: 'كيف يمكننا مساعدتك؟',
    send: 'إرسال الرسالة', sending: 'جارٍ الإرسال…', sent: 'تم إرسال رسالتك. شكرًا لتواصلك معنا.',
    error: 'تعذّر إرسال رسالتك. حاول مجددًا أو راسلنا على hello@heglo.io.',
    rateLimit: 'تم إرسال رسائل كثيرة. انتظر دقيقة أو راسلنا على hello@heglo.io.',
    unavailable: 'راسلنا مباشرة على hello@heglo.io.',
    privacy: 'سنستخدم بياناتك للرد على استفسارك.',
  },
};

export function ContactSection({ language, apiBaseUrl, startupContext }: { language: Language; apiBaseUrl: string; startupContext?: string }) {
  const text = copy[language];
  const [available, setAvailable] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error' | 'rateLimit'>('idle');
  const submitting = useRef(false);
  const endpoint = `${apiBaseUrl.replace(/\/$/, '')}/marketing/contact`;

  useEffect(() => {
    const controller = new AbortController();
    fetch(endpoint, { signal: controller.signal, credentials: 'omit' })
      .then(async (response) => response.ok ? response.json() : { available: false })
      .then((result) => { if (!controller.signal.aborted) setAvailable(result.available === true); })
      .catch(() => { if (!controller.signal.aborted) setAvailable(false); });
    return () => controller.abort();
  }, [endpoint]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    const form = event.currentTarget;
    const values = new FormData(form);
    submitting.current = true;
    setStatus('sending');
    try {
      const response = await fetch(endpoint, {
        method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(30_000),
        body: JSON.stringify({ name: values.get('name'), email: values.get('email'), message: startupContext ? `[${startupContext}]\n${values.get('message')}` : values.get('message'), website: values.get('website') }),
      });
      if (response.status === 429) { setStatus('rateLimit'); return; }
      if (!response.ok) throw new Error('Contact unavailable');
      const result = await response.json();
      if (result.success !== true) throw new Error('Contact not sent');
      setStatus('sent');
      form.reset();
    } catch { setStatus('error'); }
    finally { submitting.current = false; }
  }

  return <section className="contact-section" id="Contact" aria-labelledby="contact-heading"><div className="section-shell contact-grid">
    <div><span className="eyebrow">{text.label}</span><h2 id="contact-heading">{text.title}</h2><p>{text.description}</p><a className="contact-email" href="mailto:hello@heglo.io"><Mail size={22} aria-hidden="true" /><bdi>hello@heglo.io</bdi></a></div>
    {available ? <form className="contact-form" onSubmit={submit}>
      <label htmlFor="contact-name">{text.name}</label><input id="contact-name" name="name" autoComplete="name" required maxLength={100} pattern=".*\S.*" />
      <label htmlFor="contact-email">{text.email}</label><input id="contact-email" name="email" type="email" autoComplete="email" dir="ltr" required maxLength={254} />
      {startupContext && <p className="contact-startup-context">{language === 'ar' ? 'فكرة مشروعك: ' : 'Your startup idea: '}{startupContext}</p>}
      <label htmlFor="contact-message">{text.message}</label><textarea id="contact-message" name="message" required minLength={10} maxLength={startupContext ? 3800 : 4000} rows={5} />
      <div className="contact-trap" aria-hidden="true"><label htmlFor="contact-website">Website</label><input id="contact-website" name="website" tabIndex={-1} autoComplete="off" /></div>
      <p className="contact-privacy">{text.privacy}</p>
      <button className="button" disabled={status === 'sending'} type="submit">{status === 'sending' ? text.sending : text.send}<ArrowRight size={18} aria-hidden="true" /></button>
      <p role="status" aria-live="polite" className={`contact-status contact-status-${status}`}>{status === 'sent' || status === 'error' || status === 'rateLimit' ? text[status] : ''}</p>
    </form> : <div className="contact-direct"><Mail size={32} aria-hidden="true" /><p>{text.unavailable}</p>{startupContext && <p>{startupContext}</p>}<a className="button" href={`mailto:hello@heglo.io${startupContext ? `?subject=${encodeURIComponent(startupContext)}` : ''}`}>{text.label}<ArrowRight size={18} aria-hidden="true" /></a></div>}
  </div></section>;
}
